import { useEffect, useState } from 'react';

/**
 * PWA Detection Hook
 * 
 * Detects if the app is running in PWA (standalone) mode and applies
 * appropriate CSS classes and optimizations for the best PWA experience.
 * 
 * Features:
 * - Standalone mode detection
 * - iOS PWA detection
 * - CSS class management
 * - Viewport optimization
 */

interface PWAState {
  isStandalone: boolean;
  isIOSPWA: boolean;
  isPWACapable: boolean;
  displayMode: string;
}

export function usePWADetection() {
  const [pwaState, setPWAState] = useState<PWAState>({
    isStandalone: false,
    isIOSPWA: false,
    isPWACapable: false,
    displayMode: 'browser'
  });

  useEffect(() => {
    // Detect standalone mode
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                        (window.navigator as any).standalone === true ||
                        document.referrer.includes('android-app://');

    // Detect iOS PWA
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) ||
                  (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
    const isIOSPWA = isIOS && isStandalone;

    // Check if PWA capable
    const isPWACapable = 'serviceWorker' in navigator && 'PushManager' in window;

    // Get display mode
    let displayMode = 'browser';
    if (isStandalone) {
      displayMode = 'standalone';
    } else if (window.matchMedia('(display-mode: minimal-ui)').matches) {
      displayMode = 'minimal-ui';
    } else if (window.matchMedia('(display-mode: fullscreen)').matches) {
      displayMode = 'fullscreen';
    }

    const newState: PWAState = {
      isStandalone,
      isIOSPWA,
      isPWACapable,
      displayMode
    };

    setPWAState(newState);

    // Apply CSS classes based on PWA state
    const body = document.body;
    const root = document.documentElement;

    // Remove existing PWA classes
    body.classList.remove('pwa-standalone', 'pwa-ios', 'pwa-capable', 'pwa-mode');
    root.classList.remove('pwa-standalone', 'pwa-ios', 'pwa-capable', 'pwa-mode');

    // Add appropriate classes
    if (isStandalone) {
      body.classList.add('pwa-standalone', 'pwa-mode');
      root.classList.add('pwa-standalone', 'pwa-mode');
    }

    if (isIOSPWA) {
      body.classList.add('pwa-ios');
      root.classList.add('pwa-ios');
    }

    if (isPWACapable) {
      body.classList.add('pwa-capable');
      root.classList.add('pwa-capable');
    }

    // Set CSS custom properties
    root.style.setProperty('--pwa-display-mode', displayMode);
    root.style.setProperty('--pwa-standalone', isStandalone ? '1' : '0');

    // Log PWA state for debugging
    console.log('PWA Detection:', {
      isStandalone,
      isIOSPWA,
      isPWACapable,
      displayMode,
      userAgent: navigator.userAgent,
      standalone: (window.navigator as any).standalone
    });

  }, []);

  // Listen for display mode changes
  useEffect(() => {
    const mediaQueries = [
      window.matchMedia('(display-mode: standalone)'),
      window.matchMedia('(display-mode: minimal-ui)'),
      window.matchMedia('(display-mode: fullscreen)')
    ];

    const handleDisplayModeChange = () => {
      // Re-run detection when display mode changes
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches ||
                          (window.navigator as any).standalone === true;

      let displayMode = 'browser';
      if (isStandalone) {
        displayMode = 'standalone';
      } else if (window.matchMedia('(display-mode: minimal-ui)').matches) {
        displayMode = 'minimal-ui';
      } else if (window.matchMedia('(display-mode: fullscreen)').matches) {
        displayMode = 'fullscreen';
      }

      setPWAState(prev => ({
        ...prev,
        isStandalone,
        displayMode
      }));

      // Update CSS classes
      const body = document.body;
      const root = document.documentElement;

      if (isStandalone) {
        body.classList.add('pwa-standalone', 'pwa-mode');
        root.classList.add('pwa-standalone', 'pwa-mode');
      } else {
        body.classList.remove('pwa-standalone', 'pwa-mode');
        root.classList.remove('pwa-standalone', 'pwa-mode');
      }

      root.style.setProperty('--pwa-display-mode', displayMode);
      root.style.setProperty('--pwa-standalone', isStandalone ? '1' : '0');
    };

    // Add listeners
    mediaQueries.forEach(mq => mq.addListener(handleDisplayModeChange));

    // Cleanup
    return () => {
      mediaQueries.forEach(mq => mq.removeListener(handleDisplayModeChange));
    };
  }, []);

  return pwaState;
}

/**
 * PWA Installation Hook
 * 
 * Provides utilities for PWA installation prompts and management
 */
export function usePWAInstallation() {
  const [installPrompt, setInstallPrompt] = useState<any>(null);
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    const handleBeforeInstallPrompt = (e: Event) => {
      // Prevent the mini-infobar from appearing on mobile
      e.preventDefault();
      setInstallPrompt(e);
      setIsInstallable(true);
    };

    const handleAppInstalled = () => {
      setInstallPrompt(null);
      setIsInstallable(false);    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const promptInstall = async () => {
    if (!installPrompt) return false;

    try {
      const result = await installPrompt.prompt();
      const outcome = await result.userChoice;
      
      if (outcome === 'accepted') {
        setInstallPrompt(null);
        setIsInstallable(false);
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  return {
    isInstallable,
    promptInstall
  };
}

/**
 * Combined PWA Hook
 * 
 * Combines detection and installation capabilities
 */
export function usePWA() {
  const detection = usePWADetection();
  const installation = usePWAInstallation();

  return {
    ...detection,
    ...installation
  };
}
